import { useCallback, useMemo } from "react";
import DotGrid from "../DotGrid.jsx";
import { SWIMLANE } from "../../lib/gridMetrics.js";
import { useThemeVersion } from "../../lib/theme.js";
import { formatValue } from "../../lib/kernelFormat.js";
import { KernelEmpty } from "./shared.jsx";
import { buildGrid, cellKey } from "./heatmapGrid.js";

export const kind = "heatmap";

// The heatmap reuses DotGrid — the same dot-matrix engine as the Cluster
// swimlane — so it shares the dashboard's established heatmap language: one dot
// per (row, column) cell where size AND brightness both scale with the cell's
// value (√-scaled against the peak, matching the swimlane), empty cells show a
// faint placeholder so absence reads, and hovering a cell surfaces its value.
// Axis ordering (chronological x, busiest-row-first y) is handled in buildGrid.
const ROW_H = SWIMLANE.ROW_TARGET;       // 26
const HEADER_H = SWIMLANE.HEADER_H;      // 28
const LABEL_W = SWIMLANE.LABEL_W;        // 140
const DOT_MAX_R = SWIMLANE.DOT_MAX_R;    // 11
const MAX_BODY_H = 460;                   // scroll rows beyond this

// Shorten an ISO date (YYYY-MM-DD) to M/D for a compact axis header; other
// labels pass through (they're already thinned by the col builder).
function fmtAxisLabel(s) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) return `${Number(m[2])}/${Number(m[3])}`;
    return s;
}

export function Component({ spec, data, fillHeight = false }) {
    const grid = useMemo(() => buildGrid(spec, data), [spec, data]);

    const rows = useMemo(
        () => (grid ? grid.yKeys.map((k, i) => ({ id: `y${i}`, name: String(grid.yLabels?.[i] ?? k) })) : []),
        [grid],
    );
    // Column headers thin to ~8 visible labels (matches the swimlane) so a
    // 30-bucket axis doesn't overlap into a smear.
    const cols = useMemo(() => {
        if (!grid) return [];
        const step = Math.max(1, Math.ceil(grid.xKeys.length / 8));
        return grid.xKeys.map((k, i) => ({
            id: `x${i}`,
            name: i % step === 0 ? fmtAxisLabel(String(grid.xLabels?.[i] ?? k)) : "",
        }));
    }, [grid]);

    // Canvas fillStyle can't resolve CSS vars, so a raw "var(--green)" silently
    // fails to the muted fallback (the dots-are-gray bug). Resolve spec.color to
    // a concrete value; when unset, return null so DotGrid uses its own
    // theme-resolved green. Re-resolves on theme change.
    const themeVersion = useThemeVersion();
    const dotColor = useMemo(() => {
        const c = (spec.color || "").trim();
        if (!c) return null;
        const m = /^var\((--[^)]+)\)$/.exec(c);
        if (!m) return c; // already a concrete color (hex/rgb)
        const v = getComputedStyle(document.documentElement).getPropertyValue(m[1]).trim();
        return v || null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spec.color, themeVersion]);
    // For the HTML legend swatches, a CSS var works directly.
    const legendColor = spec.color || "var(--green)";

    const cellFn = useCallback((ri, ci, _row, _col, dims) => {
        if (!grid) return null;
        const v = grid.cells.get(cellKey(grid.xKeys[ci], grid.yKeys[ri])) ?? 0;
        // Size the radius against the ACTUALLY drawn cell (DotGrid clamps to fit),
        // same as the swimlane, so dots never bleed into neighbors.
        const dCellW = dims?.cellW ?? 24;
        const dRowH = dims?.rowH ?? ROW_H;
        const maxRadius = Math.max(1.2, Math.min(DOT_MAX_R, Math.min(dRowH, dCellW) * 0.38));
        const minRadius = Math.max(0.8, maxRadius * 0.2);
        const placeholderR = Math.max(1.4, maxRadius * 0.22);
        if (v <= 0) return { placeholder: { r: placeholderR, opacity: 0.2 } };
        // √-scale both size and brightness against the peak: keeps rank order
        // while spreading the mid-range instead of crushing it under one outlier.
        const norm = Math.sqrt(v / Math.max(grid.max, 1));
        const dot = {
            r: minRadius + norm * (maxRadius - minRadius),
            opacity: Math.max(0.22, Math.min(1, norm)),
        };
        // Omit color when unresolved so DotGrid paints its theme-resolved green.
        if (dotColor) dot.color = dotColor;
        return { dot };
    }, [grid, dotColor]);

    const cellTooltipFn = useCallback((ri, ci) => {
        if (!grid) return null;
        const v = grid.cells.get(cellKey(grid.xKeys[ci], grid.yKeys[ri])) ?? 0;
        const yName = String(grid.yLabels?.[ri] ?? grid.yKeys[ri]);
        const xName = String(grid.xLabels?.[ci] ?? grid.xKeys[ci]);
        return (
            <div className="leading-[1.35]">
                <div className="font-medium text-[color:var(--ink)]">{yName}</div>
                <div className="text-[color:var(--muted)]">
                    {xName} · {formatValue(v, spec.format || "integer")}
                </div>
            </div>
        );
    }, [grid, spec.format]);

    if (!grid || !grid.cells.size) {
        return <KernelEmpty message={spec.empty || "no rows"} />;
    }

    const naturalH = HEADER_H + rows.length * ROW_H + 6;
    const scrollable = naturalH > MAX_BODY_H;
    const bodyH = scrollable ? MAX_BODY_H : naturalH;

    return (
        <div className={`rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]${fillHeight ? " flex h-full min-h-0 flex-col" : ""}`}>
            {spec.title ? (
                <div className="mb-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">
                    {spec.title}
                </div>
            ) : null}
            <div
                className={`rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px]${fillHeight ? " flex-1 min-h-0" : ""}`}
                style={fillHeight ? undefined : { height: `${bodyH}px` }}
            >
                <DotGrid
                    rows={rows}
                    cols={cols}
                    cellW={9999}
                    rowH={ROW_H}
                    labelW={LABEL_W}
                    headerH={HEADER_H}
                    colHeaderLines={1}
                    rowLabelAlign="left"
                    cellFn={cellFn}
                    cellTooltipFn={cellTooltipFn}
                    scrollable={scrollable}
                />
            </div>
            <HeatmapLegend color={legendColor} max={grid.max} format={spec.format} />
        </div>
    );
}

// Compact legend in the swimlane's visual language: increasing dots for
// size + brightness, plus the peak value and the hover hint.
function HeatmapLegend({ color, max, format }) {
    return (
        <div className="mt-[5px] flex items-center gap-x-[10px] whitespace-nowrap text-[10px] leading-[1.1] text-[color:var(--muted-soft)]">
            <span className="flex shrink-0 items-center gap-[6px]">
                <span className="inline-flex items-center gap-[3px]">
                    <span className="inline-block h-[3px] w-[3px] rounded-full opacity-40" style={{ backgroundColor: color }} />
                    <span className="inline-block h-[5px] w-[5px] rounded-full opacity-70" style={{ backgroundColor: color }} />
                    <span className="inline-block h-[8px] w-[8px] rounded-full" style={{ backgroundColor: color }} />
                </span>
                <span>size &amp; brightness · value</span>
            </span>
            <span className="ml-auto shrink-0 tabular-nums">max {formatValue(max, format || "integer")}</span>
            <span className="shrink-0">hover a cell to inspect</span>
        </div>
    );
}
